import { serverClient } from '@obvio/server'

import { schema } from '@/api/schema'
import { CategoryLayout } from '@/components/Store/Category/CategoryLayout'
import { StoreLayout } from '@/components/Store/StoreLayout'

import type { Schema } from '@obvio/app'
import type { AllowUndefinedForOptional, DeepPick } from '@obvio/utils'
import type {
  GetStaticPathsResult,
  GetStaticPropsContext,
  GetStaticPropsResult,
} from 'next'
import type { ReactElement } from 'react'

const CATEGORY_SELECT = {
  id: true,
  title: true,
  slug: true,
  description: true,
  ageRestriction: true,
  products: {
    id: true,
    title: true,
    images: true,
    slug: true,
    category: {
      title: true,
    },
    price: {
      price: true,
      currency: true,
    },
  },
} as const

export type CATEGORY_DATA = DeepPick<
  Schema['ProductCategory'],
  typeof CATEGORY_SELECT
>

const CATEGORIES_SELECT = {
  id: true,
  slug: true,
  title: true,
} as const

export type CATEGORIES_DATA = DeepPick<
  Schema['ProductCategory'],
  typeof CATEGORIES_SELECT
>

export async function getStaticPaths(): Promise<GetStaticPathsResult> {
  const client = await serverClient(schema, { isAdmin: true })

  const categories = await client.query('getProductCategories', {
    select: {
      slug: true,
    },
  })

  const paths = categories.map(({ slug }) => ({ params: { slug } }))

  return { paths, fallback: false }
}

export async function getStaticProps(
  ctx: GetStaticPropsContext<{ slug: string }>,
): Promise<GetStaticPropsResult<CategoryProps>> {
  const client = await serverClient(schema, { isAdmin: true })

  const slug = ctx?.params?.slug

  if (!slug) {
    return { notFound: true }
  }

  const category = await client.query('getProductCategory', {
    args: {
      slug,
    },
    select: CATEGORY_SELECT,
  })

  if (!category) {
    return { notFound: true }
  }

  const categories = await client.query('getProductCategories', {
    select: CATEGORIES_SELECT,
  })

  return {
    ...client.ssrData({ category, categories }),
    revalidate: 180,
  }
}

type CategoryProps = AllowUndefinedForOptional<{
  category: CATEGORY_DATA
  categories: CATEGORIES_DATA[]
}>

function Category({ category, categories }: CategoryProps): ReactElement {
  return (
    <StoreLayout>
      <CategoryLayout
        category={category}
        products={category.products}
        categories={categories}
      />
    </StoreLayout>
  )
}

export default Category
